import type { DataFunctionArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import PageLayout from '~/modules/design-board/component/page-layout/PageLayout';
import { DESIGN_BOARD_HOME_LINK } from '~/modules/design-board/config';
import BoardDetail from '~/modules/design-board/component/board-detail/BoardDetail';

import { pluralize } from '~/utils';

import { ManageModal } from '@modules/design-board/component/manage/ManageModal';
import { CollaboratorsOverview } from '@modules/design-board/component/manage/collaborator-overview/CollaboratorsOverview';
import { DesignBoardCollaboratorsContextProvider } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import { DesignBoardPageContextProvider } from '@modules/design-board/component/context';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { MobileShareBoardModal } from '@modules/design-board/component/share-modal/MobileShareBoardModal';
import { acceptCollaborationInviteLoader } from '@modules/design-board/loaders/accept-collaboration-invite-loader.server';
import { designBoardDetailLoader } from '@modules/design-board/loaders/detail-page-loader.server';
import { followBoardFromSignInLoader } from '@modules/design-board/loaders/follow-board-from-sign-in-loader.server';
import { InviteCollaboratorsForm } from '@modules/design-board/component/manage/InviteCollaboratorForm';

import type { ActionFunctionArgs } from '@remix-run/node';

import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getActionResolver } from '@modules/design-board/actions';
import { doNotRevalidateAfterUpload as shouldRevalidate } from '~/modules/design-board/component/upload/DoNotRevalidateAfterUpload';
import type { APHandle } from '~/modules/root';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;

        const dataTitle = data?.boardDetailData?.Title;
        const title = createMetaTitle(props, [
            `${
                typeof dataTitle === 'string' ? dataTitle : 'User Design Board'
            } | Design Boards`,
            `User Design Boards`,
        ]);

        const description = createMetaDescription([
            `${data?.boardDetailData?.Content} | Design Boards`,
            `Design Board Detail description`,
        ]);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
        ];
    }
);

export const handle: APHandle = {
    isBoardDetailPage: true,
};

const action = async (args: ActionFunctionArgs) => {
    const request = await args.request.clone();
    const url = new URL(args.request.url);
    const formData = await args.request.formData();

    const handler = getActionResolver(formData, url.searchParams);

    return handler(args.context, request, url);
};

type LoaderHandlerType =
    | 'design-board-detail'
    | 'accept-invite'
    | 'follow-board';

const getLoaderHandlerType = (args: DataFunctionArgs): LoaderHandlerType => {
    const { request } = args;
    const searchParams = new URL(request.url).searchParams;

    const isAcceptingInvite =
        searchParams.has('invite') &&
        searchParams.has('invite_type') &&
        searchParams.get('invite_type') === 'DesignBoard';

    const isFollowingBoardFromSignIn = searchParams.has('follow');

    if (isAcceptingInvite) {
        return 'accept-invite';
    }

    if (isFollowingBoardFromSignIn) {
        return 'follow-board';
    }

    return 'design-board-detail';
};

export const loader = async (args: DataFunctionArgs) => {
    switch (await getLoaderHandlerType(args)) {
        case 'design-board-detail':
            return await designBoardDetailLoader(args);
        case 'accept-invite':
            return acceptCollaborationInviteLoader(args);
        case 'follow-board':
            return followBoardFromSignInLoader(args);
    }
};

const DesignBoardDetailPage = () => {
    const {
        boardDetailData,
        allPinsData,
        pagination,
        pinCounts,
        targetType,
        collaborators,
    } = useLoaderData<typeof loader>();

    const { desktop } = useAppDisplayModeContext();

    return (
        <>
            <PageLayout
                mainTitle={boardDetailData.Title}
                subTitle={
                    desktop
                        ? pluralize(pagination.totalCount, 'Pin')
                        : `${pagination.totalCount}`
                }
                backLink="/member/design-boards"
            >
                <DesignBoardPageContextProvider>
                    <DesignBoardCollaboratorsContextProvider
                        boardId={boardDetailData.ID}
                        boardName={boardDetailData.Title}
                    >
                        {desktop ? (
                            <ManageModal
                                render={{
                                    left: (
                                        <InviteCollaboratorsForm
                                            currentBoardData={boardDetailData}
                                            shareLink={`${DESIGN_BOARD_HOME_LINK}/${boardDetailData.URLSegment}`}
                                            shareImageUrl={
                                                allPinsData?.[0]?.PhotoTile
                                            }
                                            shareTitle={boardDetailData.Title}
                                            isPublic={boardDetailData.Public}
                                            collaborators={collaborators}
                                        />
                                    ),
                                    right: <CollaboratorsOverview />,
                                }}
                            />
                        ) : (
                            <MobileShareBoardModal
                                currentBoardData={boardDetailData}
                                collaborators={collaborators}
                            />
                        )}

                        <BoardDetail
                            allPinsData={allPinsData}
                            boardDetailData={boardDetailData}
                            pagination={pagination}
                            targetType={targetType}
                            pinCounts={pinCounts}
                        />
                    </DesignBoardCollaboratorsContextProvider>
                </DesignBoardPageContextProvider>
            </PageLayout>
        </>
    );
};

export { action, shouldRevalidate };

export default DesignBoardDetailPage;
